import { faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import { faArrowLeft, faPlus, faRocketLaunch, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useEffect } from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";

import { notification, Popconfirm } from "antd";
import { globalAppConfig } from "../../../config";
import Helper from "../../../Helper";
import { ExecutiveCommandSection } from "../../../models/ExecutiveCommandSection";
import { ExecutiveCommandView } from "../../../models/ExecutiveCommandView";
import PlotAPI from "../../../plotAPI";
import ExecutiveCommandName from "./ExecutiveCommandName";
import ExecutiveCommandSelectMenu from "./ExecutiveCommandSelectMenu";

type ExecutiveCommandViewSelectProps = {
    launchExecutiveCommand: (view: ExecutiveCommandView) => void;
    commandCenterViewOptions: any[];
};

export const ExecutiveCommandViewSelect: React.FC<ExecutiveCommandViewSelectProps> = ({
    launchExecutiveCommand,
    commandCenterViewOptions,
}) => {
    const context = React.useContext(GlobalStoreContext);

    const [executiveCommandViews, setExecutiveCommandViews] = React.useState<ExecutiveCommandView[]>([]);
    const [nameModal, setNameModal] = React.useState(false);

    const [selectedView, setSelectedView] = React.useState<ExecutiveCommandView | null>(null);

    const getExecutiveCommandViews = () => {
        PlotAPI.getExecutiveCommandViews(context.currentTeam)
            .then((response) => {
                const views = response.map((view: ExecutiveCommandView) =>
                    ExecutiveCommandView.fromApiObject(view, "UTC")
                );
                setExecutiveCommandViews(views);
                return views;
            })
            .then((views) => {
                const cookie = Helper.getCookie("executive_command_view");
                if (cookie) {
                    const foundView = views.find((v: ExecutiveCommandView) => v.id === parseInt(cookie));
                    if (foundView) {
                        setSelectedView(foundView);
                        return;
                    }
                }
                if (views.length > 0) {
                    setSelectedView(views[0]);
                }
            });
    };

    useEffect(() => {
        getExecutiveCommandViews();
    }, []);

    const selectView = (event: SelectChangeEvent) => {
        setSelectedView(
            executiveCommandViews.find((view) => {
                return view.id === parseInt(event.target.value);
            }) || null
        );
    };

    const onViewUpdateChange = (section: ExecutiveCommandSection | undefined) => {
        let tempSelectedView;
        if (selectedView === null || section === undefined) {
            tempSelectedView = ExecutiveCommandView.fromApiObject(
                {
                    name: "",
                    team_id: context.currentTeam.id,
                    user_id: context.userInfo?.id,
                    executive_command_sections: [],
                },
                "UTC"
            );
        } else {
            tempSelectedView = Object.create(selectedView) as ExecutiveCommandView;
        }

        const sectionIndex = selectedView?.executiveCommandSections?.findIndex((s) => s.position == section?.position);
        if (sectionIndex === undefined || sectionIndex === -1 || !selectedView) {
            tempSelectedView.executiveCommandSections = [...tempSelectedView.executiveCommandSections, section];
        } else {
            tempSelectedView.executiveCommandSections[sectionIndex] = section as ExecutiveCommandSection;
        }

        setSelectedView(tempSelectedView);
    };

    const saveView = () => {
        setNameModal(true);
    };

    const createNewView = () => {
        const newView = ExecutiveCommandView.fromApiObject(
            {
                id: -1,
                name: "New View",
                team_id: context.currentTeam.id,
                user_id: context.userInfo?.id,
                executive_command_sections: [],
            },
            "UTC"
        );
        setExecutiveCommandViews([...executiveCommandViews, newView]);
        setSelectedView(newView);
    };

    const deleteView = (viewId: number) => {
        if (!viewId) {
            return;
        }
        if (viewId < 0) {
            setExecutiveCommandViews((prevViews) => prevViews.filter((view) => view.id !== viewId));
            setSelectedView(null);
            return;
        }
        PlotAPI.deleteExecutiveCommandView(context.currentTeam, viewId).then((res) => {
            if (!res || res.error) {
                notification.error({
                    message: "Error",
                    description: "There was an error deleting the Executive Command View.",
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                });
                return;
            }
            setExecutiveCommandViews((prevViews) => prevViews.filter((view) => view.id !== viewId));
            setSelectedView(null);

            notification.success({
                message: "Success",
                description: "Executive Command View deleted successfully.",
                duration: globalAppConfig.NOTIFICATION_DURATION,
            });
        });
    };

    return (
        <div className="flex flex-col h-full w-full overflow-hidden">
            <div className="w-full flex flex-row overflow-hidden p-2! gap-4">
                <IconButton size="large" onClick={() => window.history.back()}>
                    <FontAwesomeIcon icon={faArrowLeft} className="text-white" />
                </IconButton>
                <FormControl fullWidth size="medium" variant="standard" className="p-4!">
                    <InputLabel id="select-label" className="text-lg!">
                        Select an Executive Command View
                    </InputLabel>
                    <Select
                        value={executiveCommandViews.find((v) => v.id === selectedView?.id)?.id || ""}
                        className="w-full text-lg!"
                        label="Select Executive Command View"
                        onChange={selectView}
                    >
                        {executiveCommandViews.map((view) => (
                            <MenuItem key={view.id} value={view.id} className="text-lg!">
                                {view.name}
                            </MenuItem>
                        ))}
                        {executiveCommandViews.length === 0 && (
                            <MenuItem disabled className="text-lg!">
                                No Executive Command Views Found.
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <Popconfirm
                    title="Delete the View"
                    description="Are you sure to delete this view?"
                    onConfirm={() => deleteView(selectedView?.id as number)}
                    onCancel={() => {}}
                    okText="Confirm"
                    cancelText="Cancel"
                >
                    <button className=" hover:cursor-pointer hover:bg-plot-darkmode-white/60 py-4 px-2 rounded-lg text-white font-semibold text-lg text-nowrap flex-nowrap">
                        <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
                        Delete View
                    </button>
                </Popconfirm>
                <button
                    onClick={createNewView}
                    className=" hover:cursor-pointer hover:bg-plot-darkmode-white/60 py-4 px-2 rounded-lg text-white font-semibold text-lg text-nowrap flex-nowrap"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create New
                </button>
                <button
                    onClick={saveView}
                    className=" hover:cursor-pointer hover:bg-plot-darkmode-white/60 py-4 px-2 rounded-lg text-white font-semibold text-lg text-nowrap flex-nowrap"
                >
                    <FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />
                    Save Executive Command View
                </button>
            </div>
            <div className="grid grid-cols-2 p-2 grid-rows-2 gap-2 w-full h-full" style={{ zoom: 0.5 }}>
                {Array.from({ length: 4 }).map((_, idx) => (
                    <ExecutiveCommandSelectMenu
                        position={idx}
                        key={idx}
                        options={commandCenterViewOptions}
                        onChange={onViewUpdateChange}
                        section={selectedView?.executiveCommandSections?.find((section) => section.position === idx)}
                        selectedView={selectedView}
                    />
                ))}
            </div>
            <div className="w-full flex flex-row overflow-hidden mb-2 gap-2">
                <button
                    className="gradient-button bg-plot-darkmode-white/30! hover:cursor-pointer hover:bg-plot-darkmode-white/60  w-full py-8 rounded-lg text-white font-semibold text-lg"
                    onClick={() => {
                        if (!selectedView) {
                            notification.error({
                                message: "No Executive Command View Selected",
                                description: "Please select an Executive Command View to launch.",
                                duration: globalAppConfig.NOTIFICATION_DURATION,
                            });
                            return;
                        }
                        if (selectedView.id < 0 || !selectedView.id) {
                            notification.error({
                                message: "Executive Command View Not Saved",
                                description: "Please save the Executive Command View before launching.",
                                duration: globalAppConfig.NOTIFICATION_DURATION,
                            });
                            return;
                        }
                        launchExecutiveCommand(selectedView);
                    }}
                >
                    <FontAwesomeIcon icon={faRocketLaunch} className="mr-2" />
                    Launch Executive Command
                </button>
            </div>
            <ExecutiveCommandName
                open={nameModal}
                refreshViews={getExecutiveCommandViews}
                toggleModal={() => setNameModal(!nameModal)}
                selectedView={selectedView}
            />
        </div>
    );
};
